import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import RecipeResultCard from "components/RecipeResultCard";
import searchEmptyState from "components/SlotSwapPanel/searchEmptyState";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import { recipeDietLabel } from "utils/dietPreference";
import { RED, BORDER, MUTED } from "assets/theme/brandColors";

/**
 * Swap one slot of a schedule for another recipe.
 *
 * Lifted verbatim out of the schedule builder's review overlay, which is where
 * this was worked out: a bounded set of slot-scoped alternatives (ranked by the
 * backend against the rest of the week, with the reasoning annotation each one
 * carries), a capped search within the slot, and "Surprise me" for the auto
 * pick. It now serves the committed schedule view too — the same panel, the
 * same endpoints, the same suggestions. There is exactly one swap
 * implementation, and this is it.
 *
 * The POST lives here rather than in each caller so both surfaces log the same
 * taste signal: picking a named alternative is an endorsement, "Surprise me" is
 * only a rejection, and the backend tells those apart by whether a
 * `new_recipe_id` was sent. Callers just apply `onReplaced(newRecipe)` to their
 * own list.
 *
 * Colours are palette-only: RED on #fff and #222 on #fff for the controls,
 * MUTED for secondary text (5.3:1 on white).
 */

// The endpoint enforces this too (`q` has min_length=2); repeating it here
// keeps the debounce from firing a request the server will reject.
const MIN_QUERY_LENGTH = 2;
// Long enough that typing a word costs one request rather than five, short
// enough that the results feel attached to the keystrokes.
const SEARCH_DEBOUNCE_MS = 275;

function SlotSwapPanel({ programId, slot, onReplaced, onError, onClose }) {
  const [alternatives, setAlternatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // `results` holds the last set a search actually came back with, and
  // `searchedTerm` names the query it belongs to. The empty state is rendered
  // off those two rather than off the text in the box, which is the whole
  // point: "Nothing matched" may only ever describe a search that ran. A
  // tester read it as a statement about the catalog and nearly concluded there
  // were no egg recipes, while "Buttered Egg Spätzle" sat a few inches above.
  const [results, setResults] = useState(null);
  const [searchedTerm, setSearchedTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Monotonic request id. Debounced typing means several searches can be in
  // flight, and without this the slowest one wins.
  const requestSeq = useRef(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setAlternatives([]);
    setResults(null);
    setSearchedTerm("");
    setSearchFailed(false);
    setQuery("");
    setLoading(true);
    (async () => {
      try {
        const res = await apiClient.get(ENDPOINTS.schedule.slotAlternatives(programId, slot.id, 5));
        if (!cancelled) setAlternatives(res.data.alternatives || []);
      } catch (err) {
        console.error("Failed to load alternatives:", err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId, slot.id]);

  const runSearch = async (rawTerm) => {
    const term = (rawTerm ?? "").trim();
    if (term.length < MIN_QUERY_LENGTH) return;
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setSearching(true);
    setSearchFailed(false);
    try {
      const res = await apiClient.get(ENDPOINTS.schedule.recipeSearch, {
        params: { q: term, meal_type: slot.meal_type || undefined, limit: 10 },
      });
      if (seq !== requestSeq.current) return;
      setResults(res.data.results || []);
      setSearchedTerm(term);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // A request that failed is not an empty catalog. Leaving `results` null
      // keeps the zero-results copy off the screen and says what happened.
      setResults(null);
      setSearchedTerm("");
      setSearchFailed(true);
      console.error("Recipe search failed:", err.message);
    } finally {
      if (seq === requestSeq.current) setSearching(false);
    }
  };

  // Search-as-you-type. The panel used to fire only on Enter, so the results
  // and the query could disagree for as long as the user kept typing.
  useEffect(() => {
    const term = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (term.length < MIN_QUERY_LENGTH) {
      // Back to the slot's own suggestions, and nothing in flight may land on
      // top of them.
      requestSeq.current += 1;
      setResults(null);
      setSearchedTerm("");
      setSearching(false);
      setSearchFailed(false);
      return undefined;
    }

    setSearching(true);
    debounceRef.current = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, slot.meal_type]);

  const searchNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(query);
  };

  // `newRecipeId === null` is the auto pick ("Surprise me").
  const replaceWith = async (newRecipeId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await apiClient.post(
        ENDPOINTS.schedule.replaceRecipe(programId, slot.id),
        newRecipeId ? { new_recipe_id: newRecipeId } : {}
      );
      onReplaced(res.data.new_recipe, slot);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "No replacement found for this recipe type.";
      if (onError) onError(msg);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  // Below MIN_QUERY_LENGTH the panel is showing the slot's own alternatives,
  // and none of the search copy applies.
  const searchActive = query.trim().length >= MIN_QUERY_LENGTH;
  const shown = results === null ? alternatives : results;
  const emptyMessage = searchEmptyState({
    searchActive,
    searching,
    searchFailed,
    results,
    searchedTerm,
    alternativesCount: alternatives.length,
  });

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.45)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: "#fff",
          borderRadius: "14px",
          maxWidth: 440,
          width: "100%",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: "24px 24px 16px" }}>
          <Box
            sx={{
              fontSize: 16,
              fontWeight: 800,
              color: "#222",
              mb: 0.75,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Swap &ldquo;{slot.recipe_title}&rdquo;
          </Box>
          <Box
            sx={{
              fontSize: 13,
              color: MUTED,
              lineHeight: 1.5,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Pick one of these, or search for something specific.
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid", borderColor: BORDER }} />

        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {loading ? (
            <Box
              sx={{
                py: 3,
                textAlign: "center",
                fontSize: 13,
                color: MUTED,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              Finding alternatives…
            </Box>
          ) : (
            <>
              {shown.map((alt) => (
                <Box key={alt.id} sx={{ mb: 1.25 }}>
                  <RecipeResultCard
                    title={alt.recipe_title}
                    meta={[
                      alt.time_in_minutes ? `${alt.time_in_minutes} min` : null,
                      alt.cuisine,
                      recipeDietLabel(alt.diet),
                      (alt.reasons || [])[0],
                    ]}
                    onClick={() => replaceWith(alt.id)}
                  />
                </Box>
              ))}

              {shown.length === 0 && emptyMessage && (
                <Box
                  sx={{
                    py: 2,
                    textAlign: "center",
                    fontSize: 13,
                    color: MUTED,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  {emptyMessage}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box sx={{ borderTop: "1px solid", borderColor: BORDER, p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Box
              component="input"
              value={query}
              placeholder="Search recipes for this slot…"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchNow()}
              sx={{
                flex: 1,
                border: "1.5px solid",
                borderColor: BORDER,
                borderRadius: "8px",
                px: 1.25,
                py: "8px",
                fontSize: 13,
                fontFamily: "Roboto, sans-serif",
                outline: "none",
                color: "#222",
                "&:focus": { borderColor: RED },
                "&::placeholder": { color: MUTED },
              }}
            />
            <Box
              component="button"
              onClick={searchNow}
              disabled={!searchActive || searching}
              sx={{
                px: 2,
                bgcolor: !searchActive ? "#ddd" : RED,
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 700,
                cursor: !searchActive ? "default" : "pointer",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {searching ? "…" : "Search"}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.25 }}>
            <Box
              component="button"
              onClick={() => replaceWith(null)}
              disabled={submitting}
              sx={{
                flex: 1,
                padding: "10px",
                bgcolor: "#f0f0f0",
                color: "#444",
                border: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 700,
                cursor: submitting ? "default" : "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": submitting ? {} : { bgcolor: "#e4e4e4" },
              }}
            >
              {submitting ? "Swapping…" : "Surprise me"}
            </Box>
            <Box
              component="button"
              onClick={onClose}
              sx={{
                padding: "10px 18px",
                bgcolor: "#f0f0f0",
                color: "#444",
                border: "none",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Roboto, sans-serif",
                "&:hover": { bgcolor: "#e4e4e4" },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

SlotSwapPanel.propTypes = {
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // The slot being replaced. `id` is the recipe currently in it.
  slot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    recipe_title: PropTypes.string,
    meal_type: PropTypes.string,
  }).isRequired,
  // (newRecipe, replacedSlot) — the payload from the replace endpoint.
  onReplaced: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default SlotSwapPanel;
